import {
  ADMIN_ROLE,
  ENGINEER_ROLE,
  INSTALLATION_ROLE,
} from "../constants/locks";
import { FILTER_TYPES } from "../dao/types";
import moment from "moment";
import * as XLSX from "xlsx";
import { ROUTES } from "../enums/Routes";

function calculateSortorder(fieldA, fieldB) {
  return fieldA.sortOrder.overview - fieldB.sortOrder.overview;
}

export function sortColumns(columns) {
  return columns.sort(calculateSortorder);
}

/**
 * Turn a base64 string into a Blob via small slices.
 * @param {string} base64
 * @param {string} mimeType
 * @returns {Blob}
 */
export const base64ToBlob = (base64, mimeType) => {
  const sliceSize = 1024; // Chunk size in bytes (1KB)
  const byteChars = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteChars.length; offset += sliceSize) {
    const slice = byteChars.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    byteArrays.push(new Uint8Array(byteNumbers));
  }

  return new Blob(byteArrays, { type: mimeType });
};

/**
 * Function to export data (Blob or raw bytes) to a file and trigger download.
 * @param {string} fileName
 * @param {Blob|Uint8Array|string} data
 * @param {string} mimeType
 */
export function exportToFile(fileName, data, mimeType = "text/csv") {
  const blob =
    data instanceof Blob ? data : new Blob([data], { type: mimeType });

  const a = document.createElement("a");
  a.download = fileName;
  a.href = URL.createObjectURL(blob);
  a.dispatchEvent(
    new MouseEvent("click", { view: window, bubbles: true, cancelable: true })
  );
  a.remove();
}

/**
 * Function to get the sort order based on the entity
 * @param {string} entity
 */
export const getSortOrder = (entity) => {
  switch (entity) {
    case FILTER_TYPES.project:
      return [{ field: "number", sort: "asc" }];
    case FILTER_TYPES.system_group:
    case FILTER_TYPES.location:
      return [
        { field: "object_number", sort: "asc" },
        { field: "number", sort: "asc" },
      ];
    case FILTER_TYPES.system:
      return [
        { field: "object_number", sort: "asc" },
        { field: "system_group_number", sort: "asc" },
        { field: "number", sort: "asc" },
      ];
    default:
      return [{ field: "number", sort: "asc" }];
  }
};

export const getEntityReference = (entity) => {
  if (typeof entity.project_type !== "undefined") {
    return `${entity.project_type}${entity.number}`;
  } else if (typeof entity.reference2 !== "undefined") {
    return entity.reference2;
  }
  // For a cable type, there is no number but a type
  return entity?.number ?? entity?.type;
};
/*
 * Non Operation functions wrapped in an object.
 * Can be used as a default values
 * @returns {undefined}
 */
export const noop = {
  fn: () => {
    return;
  },
  resolve: () => Promise.resolve("Noop Resolve"),
  reject: () => Promise.reject(new Error("Noop Resolve")),
};

/**
 * Function to remove all soft-deleted entities from an array
 * @param {object} entities
 */
export const ejectDeletedEntities = (entities = []) => {
  if (Array.isArray(entities)) {
    return entities.filter((entity) => typeof entity.deleted === "undefined");
  }
  return [];
};

/**
 * Function to return only the ID of the options
 * @param {array} options
 * @param {string} identifier
 */
export const getIdValues = (options, identifier = "id") =>
  options ? options.map((option) => option[identifier]) : [];

/**
 * getInitials - Get the initials of a user name.
 * @param {string} username - The username of the user.
 * @returns {string} The initials of the user.
 */
export const getInitials = (username) => {
  // Return the parts of user.name which starts (\b) with an uppercase char followed by lowercase chars.
  const nameParts = username.match(/(\b[A-Z][a-z]+)/g);
  // Keep only the first two elements, then grab the first char from both.
  // guard for nameParts is undefined (no match)
  if (!nameParts) {
    return username.slice(0, 2);
  }
  return nameParts
    .slice(0, 2)
    .map((part) => part[0])
    .join("");
};

/**
 * Stops the propagation of an event.
 *
 * @param {Event} event - The event object.
 * @returns {void}
 */
export const stopPropagation = (event) => {
  if (event.stopPropagation) {
    event.stopPropagation(); // W3C model
  } else {
    event.cancelBubble = true; // IE model
  }
};

/**
 * recursive function flattenRecord
 * given input {a: {b: {c: 1}}}
 * should return {a.b.c: 1}
 * @param {object} record
 * @returns {object} flattened record
 */
export const flattenRecord = (record = {}) => {
  // guard
  if (Object.keys(record).length === 0) {
    return record;
  }
  return Object.entries(record).reduce((acc, [key, value]) => {
    if (typeof value === "object") {
      const flattenedObject = flattenRecord(value);
      Object.entries(flattenedObject).forEach(([nestedKey, nestedValue]) => {
        acc[`${key}.${nestedKey}`] = nestedValue;
      });
    } else {
      return {
        ...acc,
        [key]: value,
      };
    }
    return acc;
  }, {});
};

/**
 * Generates the inline edit query parameter based on the isInlineEdit flag.
 * @param {boolean} isInlineEdit - A boolean flag indicating whether inline editing is enabled.
 * @return {string} The generated query parameter string for inline editing. Returns "?inline_edit=true" if isInlineEdit is true, otherwise returns an empty string.
 */
export const getInlineEditQueryParam = (isInlineEdit) => {
  return isInlineEdit ? "?inline_edit=true" : "";
};

/**
 * Updates the given field based on form state and user authorization for cable type.
 *
 * This function implements a multi-layered field enabling/disabling logic:
 * 1. API-driven conditions (enabledWhen) - primary source of truth
 * 2. Role-based validation - security layer ensuring users have required permissions
 * 3. Special lock field handling - additional security for engineering/installation locks
 *
 * @param {Object} field - The field object from API containing enabledWhen conditions
 * @param {Object} formValues - The current state of the form (engineering_lock, installation_lock, etc.)
 * @param {Array} authorisation - An array of user roles for authorization
 * @return {Object} - The updated field object with the calculated disabled state
 */
export const getUpdatedFields = (field, formValues, authorisation, entityType) => {

  // Return when formValues is not an object with values
  if (typeof formValues === "object" &&
  formValues !== null &&
  Object.keys(formValues).length === 0) {
    return {
      ...field,
      disabled: false,
    };
  }

  // ============================================================================
  // STEP 0: HARD-DISABLED FIELDS
  // ============================================================================
  // Some fields are permanently disabled in the API (e.g., bundle_status)
  // These should never be enabled regardless of any other conditions
  if (field.disabled === true) {
    return { ...field, disabled: true };
  }

  // ============================================================================
  // ROLE DETECTION
  // ============================================================================
  // Extract current form lock states
  const installationLock = formValues?.installation_lock;
  const engineeringLock = formValues?.engineering_lock;

  // Determine user roles from authorization array
  const isEngineer = authorisation.some(
    (role) => role.name.toLowerCase() === ENGINEER_ROLE
  );

  const isInstallation = authorisation.some(
    (role) => role.name.toLowerCase() === INSTALLATION_ROLE
  );

  const isAdmin = authorisation.some(
    (role) => role.name.toLowerCase() === ADMIN_ROLE
  );

  // ============================================================================
  // STEP 1: API-DRIVEN LOGIC (PRIMARY SOURCE OF TRUTH)
  // ============================================================================
  // Check if current form values satisfy the field's enabledWhen conditions
  // Example: field requires { engineering_lock: false, installation_lock: false }
  // and form has { engineering_lock: false, installation_lock: false } → enabled
  const enabledWhen = field?.enabledWhen && field?.enabledWhen[entityType];
  let isEnabledByAPI = true; // Default to enabled if no conditions specified

  if (enabledWhen) {
    // Field is enabled if ANY of the enabledWhen conditions are met
    isEnabledByAPI = enabledWhen.some((condition) => {
      // ALL properties in a condition must match form values
      return Object.keys(condition).every((key) => {
        const expectedValue = condition[key];
        const actualValue = formValues[key];

       if (["true", "false"].includes(String(expectedValue))) {
          return String(actualValue) === String(expectedValue); // handle type differences
        } else if (actualValue) {
            const regex = new RegExp(expectedValue.toString().toLowerCase());
            return regex.test(actualValue.toString().toLowerCase());
        } else {
          return false;
        }
      });
    });
  }

  // ============================================================================
  // STEP 2: ROLE-BASED VALIDATION (SECURITY LAYER)
  // ============================================================================
  // Even if API conditions are met, user must have appropriate roles
  // This prevents unauthorized access even if form state matches requirements
  // Only applicable for cable entity type
  let isEnabledByRole = true; // Default to enabled for fields without role requirements

  if (enabledWhen && !isAdmin && entityType === FILTER_TYPES.cable) {
    // Admins bypass all role checks
    // Check if user has required roles for any of the satisfied conditions
    const hasValidRoleForAnyCondition = enabledWhen.some((condition) => {
      // First, verify this condition is actually met by current form values
      const conditionMet = Object.keys(condition).every((key) => {
        const expectedValue = condition[key];
        const actualValue = formValues[key];
        return actualValue === expectedValue;
      });

      // Skip role validation if this condition isn't met anyway
      if (!conditionMet) return false;

      // Determine what roles this specific condition requires
      const requiresEngineerAccess =
        Object.hasOwn(condition, "engineering_lock") &&
        condition.engineering_lock === false;
      const requiresInstallationAccess =
        Object.hasOwn(condition, "installation_lock") &&
        condition.installation_lock === true;

      // User must have all required roles for this condition
      let hasRequiredRole = true;

      // If condition requires engineering access, user must be engineer
      if (requiresEngineerAccess && !isEngineer) {
        hasRequiredRole = false;
      }

      // If condition requires installation access, user must be installation
      if (requiresInstallationAccess && !isInstallation) {
        hasRequiredRole = false;
      }

      return hasRequiredRole;
    });

    isEnabledByRole = hasValidRoleForAnyCondition;
  }

  // ============================================================================
  // STEP 3: SPECIAL LOCK FIELD HANDLING (ADDITIONAL SECURITY)
  // ============================================================================
  // Engineering and installation lock fields have extra security rules
  // beyond the standard API + role logic
  let finalDisabledState = !isEnabledByAPI || !isEnabledByRole;

  if (field.field_name === "installation_lock") {
    // Installation lock requires installation role (unless admin)
    // and cannot be changed when engineering lock is active
    const roleBasedDisabled = isAdmin
      ? !isAdmin || !engineeringLock // Admin can change unless engineering lock active
      : !isInstallation || !engineeringLock; // Non-admin needs installation role and no engineering lock

    finalDisabledState = finalDisabledState || roleBasedDisabled;
  }

  if (field.field_name === "engineering_lock") {
    // Engineering lock requires engineer role (unless admin)
    // and cannot be changed when installation lock is active
    const roleBasedDisabled = isAdmin
      ? !isAdmin // Admin can always change
      : !isEngineer || installationLock; // Non-admin needs engineer role and no installation lock

    finalDisabledState = finalDisabledState || roleBasedDisabled;
  }

  // ============================================================================
  // FINAL RESULT
  // ============================================================================
  // Field is disabled if ANY of the following are true:
  // - API conditions not met (isEnabledByAPI = false)
  // - User lacks required roles (isEnabledByRole = false)
  // - Special lock field rules apply (roleBasedDisabled = true)
  return {
    ...field,
    disabled: finalDisabledState,
  };
};

/**
 * This function was created for a very specific reason : renaming cable bundle to bundle.
 * If we use the cable_bundle which is coming from BE, then we can use this function to
 * convert cable_bundle to bundle. Below we have currently 2 cases where we use this function,
 * we can extend it if we need to.
 *
 * @param {string} type - The type of the entity.
 *
 * @return {string} - The name of the entity. If the type is cable_bundle, then it will return bundle.
 */
export const getType = (type) =>
  type === FILTER_TYPES.cable_bundle || /cable-bundles/i.test(type)
    ? "bundle"
    : type;

/**
 * This function handles the value of a field that has a pattern.
 * If the value is empty and the field has a pattern, then it should return null.
 * So that the backend can handle it as an empty value with null.
 * @param {string} value - The value of the field.
 * @param {boolean} hasPattern - A boolean flag indicating whether the field has a pattern.
 * @return {string|null} - The value of the field.
 */
export const processHasPatternInputValue = (value, hasPattern) => {
  return value === "" && hasPattern ? null : value;
};

/*
 * Function to get the sort order string
 * @param {array} sortModel e.g. [{field: "number", sort: "asc"}]
 * @param {array} columns e.g. [{field: "number", sort_name: "number", ...}]
 * @returns {string} sort order string
 */
export const sortModelToString = (sortModel, columns) => {
  if (sortModel && sortModel.length > 0) {
    const sortModelModified = sortModel.map((item) => {
      const column = columns.find((col) => col.field === item.field);
      if (column?.sort_name) {
        return {
          field: column.sort_name ?? column.field,
          sort: item.sort,
        };
      }
      return item;
    });
    return sortModelModified
      .map((item) => `${item.field} ${item.sort}`)
      .join(",");
  } else {
    return "";
  }
};

/**
 * Function to convert a sort string back to sortModel array
 * @param {string} sortString e.g. "number asc" or "name asc,type desc"
 * @param {array} columns e.g. [{field: "number", sort_name: "number", ...}]
 * @returns {array} sortModel array e.g. [{field: "number", sort: "asc"}]
 */
export const sortStringToModel = (sortString, columns = []) => {
  if (!sortString || typeof sortString !== "string") {
    return [];
  }

  return sortString.split(",").map((sortItem) => {
    const [fieldName, sortDirection] = sortItem.trim().split(/\s+/);

    // Find the column that matches either field name or sort_name
    const column = columns.find(
      (col) => col.field === fieldName || col.sort_name === fieldName
    );

    // Use the actual field name if we found a matching column, otherwise use the fieldName as-is
    const actualFieldName = column ? column.field : fieldName;

    return {
      field: actualFieldName,
      sort: sortDirection || "asc",
    };
  });
};

/**
 * function that returns the index of the selected menu item based on the path.
 * @param {string} path
 * @param {array} items
 * @returns {number} - The index of the selected menu item.
 */
export const getSelectedMenuItemIndex = (path, items) => {
  // guard - if the path is the root path, return 0
  if (path === "/") return 0;
  // guard - if the path is not a string, return -1
  if (!path || typeof path !== "string") return -1;
  // extract the first segment of the path
  const pathSegment = path.split("/")[1];
  // create a regex to match the path segment with an optional 's' at the end
  const pathMatchRegex = new RegExp(`^${pathSegment}s?$`);
  // find the index of the item that matches the path regex
  return items.findIndex((item) => {
    const itemPathSegment = item.path.split("/")[1];
    return pathMatchRegex.test(itemPathSegment);
  });
};

/**
 * fetchDataWithOptionsAndFilters - Higher order function that takes
 * a fetch function and returns a function that takes options
 * and returns a function that takes a filter string.
 * @param {function} fetchFn - The fetch function to be called.
 * @returns {function} - A function that takes options and returns a function that takes a filter string.
 */
export const fetchDataWithOptionsAndFilters =
  (fetchFn) => (options) => (filterString) => {
    return fetchFn(filterString, { ...options });
  };

/*
 * Function to convert snake case to normal case
 * @param {string} str
 * @returns {string} normal case string
 */
export const snakeCaseToNormalCase = (str) => {
  if (!str) return "";
  return str.split("_").join(" ");
};

/**
 * Function that checks in a given input is a Date object
 * @param {mixed} input
 * @returns {boolean}
 */
export const isDate = (input) => {
  return input instanceof Date;
};

/**
 * Function that converts a Date object to a string
 * value that is accepted by the API (YYYY-MM-DD)
 * @param {Date} date
 * @returns {string}
 */
export const dateToString = (date) => {
  if (!date) return "";
  return moment(date).format("YYYY-MM-DD");
};

/**
 * Function to convert snake case to camel case
 * @param {string} str - The snake case string
 * @returns {string} - The camel case string
 */
export const snakeCaseToCamelCase = (str) => {
  if (!str) return "";

  // Efficiently remove leading and trailing underscores
  const trimmedStr = str.replace(/^_+/, "").replace(/_+$/, "");

  // Convert to camel case
  return trimmedStr
    .toLowerCase()
    .replace(/(_\w)/g, (match) => match[1].toUpperCase());
};

/**
 * Toggles the disability state of specific fields based on the `final_location` value in `formValues`.
 *
 * @param {Array} fields - The array of field objects representing the current form configuration.
 * @param {Object} formValues - The current form values, expected to have a `final_location` property.
 * @return {Array} - The updated array of field objects with the modified `disabled` state for relevant fields.
 */
export const toggleFieldDisabilityBasedOnFlag = (fields, formValues) => {
  if (!Array.isArray(fields)) {
    return fields;
  }

  const finalLocationValue = !!formValues?.final_location;
  const sectionReferenceValue = !!formValues?.section_reference;

  return fields.map((field) => {
    // For fields location_id and section_reference, disable based on final_location
    if (
      field.field_name === "location_id" ||
      field.field_name === "section_reference"
    ) {
      // If final_location is true, both are disabled
      // If final_location is false and it's location_id, disable if section_reference is truthy
      if (finalLocationValue) {
        return { ...field, disabled: true };
      } else if (field.field_name === "location_id" && !finalLocationValue) {
        return { ...field, disabled: !!sectionReferenceValue };
      }
      return { ...field, disabled: false };
    }

    return field;
  });
};

export const isEquipmentSectionChanged = (entityType, formState) => {
  const isEquipment = entityType === FILTER_TYPES.equipment;
  const sectionReferenceChanged = !formState.pristine["section_reference"];

  return isEquipment && sectionReferenceChanged;
};

export const reloadPageIfEquipmentSectionChanged = (entityType, formState) => {
  if (isEquipmentSectionChanged(entityType, formState)) {
    window.location.reload();
  }
};

export const excludeLocationFieldsIfEquipmentSectionChanged = (
  changedValues,
  entityType,
  entity,
  formState
) => {
  const hasSectionReferenceValue = Boolean(entity?.section_reference);

  if (
    isEquipmentSectionChanged(entityType, formState) &&
    hasSectionReferenceValue
  ) {
    delete changedValues.location_id;
    delete changedValues.location_reference;
  }

  return changedValues;
};

export const clearLocationFieldsIfSectionReferenceIsNull = (
  event,
  fieldConfig,
  formState
) => {
  const isSectionReferenceField = fieldConfig.name === "section_reference";
  const sectionReferenceCleared = event.selectValue === null;

  if (isSectionReferenceField && sectionReferenceCleared) {
    formState.setField("location_id", null);
    formState.setField("location_reference", null);
  }
};

/**
 * Convert JSON data to CSV
 * @param {Array<Object>} jsonData
 * @param {string} separator
 * @returns {string} CSV text
 */
export const convertJsonToCsv = (
  jsonData,
  separator,
  includeHeaders = true
) => {
  // Guard - no separator provided
  if (!separator || typeof separator !== "string") {
    throw new Error("A valid separator must be provided");
  }
  // Guard - empty or invalid data
  if (!Array.isArray(jsonData) || jsonData.length === 0) return "";

  const allKeys = [...new Set(jsonData.flatMap(Object.keys))];

  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return s.includes(separator) || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };

  const header = allKeys.join(separator);
  const rows = jsonData.map((row) =>
    allKeys.map((k) => esc(row[k])).join(separator)
  );

  return includeHeaders ? [header, ...rows].join("\r\n") : rows.join("\r\n");
};

/**
 * Process and download a CSV report from base‑64 data.
 * ‑ Accepts either:   (a) base‑64‑encoded JSON       → we convert to CSV
 *                     (b) base‑64‑encoded CSV text   → we keep text
 * @param {string} encodedData Base64 encoded CSV data
 * @param {Object} options Options object
 * @param {boolean} [options.hasHeader=true] Whether to include headers when converting JSON to CSV
 * @param {string} [options.fileName="report"] Name for the downloaded file (without .csv)
 * @param {string|null} [options.csv_separator=null] If set, overrides the default separator (e.g., ";")
 * @returns {void}
 */
export const processCsvReport = (encodedData, options) => {
  const {
    hasHeader = true,
    fileName = "report",
    csv_separator = null,
  } = options;

  const decodedText = atob(encodedData);
  const separator = csv_separator ?? ";"; // default delimiter;
  /**
   * Helper that writes the final CSV (string) to disk.
   */
  const downloadCsv = (csvString) => {
    const bom = "\uFEFF"; // UTF‑8 BOM for Excel
    const blob = new Blob([bom + csvString], {
      type: "text/csv;charset=utf-8;",
    });
    exportToFile(`${fileName}.csv`, blob, "text/csv");
  };

  // ------------------------------------------------------------
  // 1. Try to treat the payload as JSON
  // ------------------------------------------------------------
  let isJsonData = false;
  let dataArray = null;

  try {
    const jsonData = JSON.parse(decodedText);
    isJsonData = true;

    // normalise to array
    if (Array.isArray(jsonData)) {
      dataArray = jsonData;
    } else if (jsonData?.data && Array.isArray(jsonData.data)) {
      dataArray = jsonData.data;
    } else {
      dataArray = [jsonData];
    }
  } catch {
    // JSON parsing failed, will treat as CSV text below
  }

  if (isJsonData && dataArray) {
    const csvBody = convertJsonToCsv(dataArray, separator, hasHeader);
    downloadCsv(csvBody);
    return;
  }

  // ------------------------------------------------------------
  // 2. Payload is already CSV text → download as-is (no sep directive)
  // ------------------------------------------------------------
  downloadCsv(decodedText);
};

/**
 * Process and download a PDF report from base64 encoded data
 * @param {string} encodedData Base64 encoded PDF data
 * @param {string} fileName Name for the downloaded file
 */
export const processPdfReport = (encodedData, fileName) => {
  const pdfBytes = new Uint8Array(
    Array.from(atob(encodedData), (c) => c.charCodeAt(0))
  );
  exportToFile(`${fileName}.pdf`, pdfBytes, "application/pdf");
};

/**
 * Process Excel data that could be either JSON or binary Excel
 * @param {string} encodedData Base64 encoded data
 * @param {string} fileName Name for the downloaded file
 * @param {string} [sheetName] Optional name for the Excel sheet
 * @returns {boolean} Success status
 */
export const processExcelReport = (
  encodedData,
  fileName,
  sheetName,
  hasHeader = true
) => {
  const excelMime =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

  // parse as JSON first (for potential Excel generation from JSON)
  try {
    const jsonText = atob(encodedData);
    const jsonData = JSON.parse(jsonText);
    const rows = jsonData.TmpQry_ExportSpreadsheet || jsonData.data || [];

    if (rows.length > 0) {
      convertJsonToExcel(rows, fileName, sheetName, hasHeader);
      return true;
    }
  } catch {
    // Not JSON, continue to Excel binary handling
  }

  // Handle as binary Excel file
  const blob = base64ToBlob(encodedData, excelMime);
  exportToFile(`${fileName}.xlsx`, blob, excelMime);
  return true;
};

/**
 * Sanitizes a string to be used as an Excel sheet name.
 * Replaces invalid characters with underscores and truncates to 31 characters.
 * Returns "Sheet1" if the sanitized name is empty.
 * @param {string} name The original name
 * @returns {string} The sanitized sheet name
 */
export const sanitizeSheetName = (name) => {
  if (!name) {
    return "Sheet1";
  }
  // Excel sheet names have a limit of 31 characters and cannot contain certain characters
  // like \ / ? * [ ] : and cannot be empty
  const invalidCharsRegex = /[\\/?*[\]:]/g;
  let safeName = name.replace(invalidCharsRegex, "_").substring(0, 31);

  if (!safeName) {
    safeName = "Sheet1";
  }
  return safeName;
};

/**
 * Convert JSON data to an Excel file (.xlsx) and download
 * @param {Array} rows Array of objects representing rows
 * @param {string} fileName Name for the downloaded file
 * @param {string} [sheetName] Optional name for the Excel sheet (defaults to fileName)
 */
export const convertJsonToExcel = (
  rows,
  fileName,
  sheetName,
  includeHeaders = true
) => {
  // worksheet from the JSON data
  const worksheet = includeHeaders
    ? XLSX.utils.json_to_sheet(rows)
    : XLSX.utils.json_to_sheet(rows, { skipHeader: true });
  // workbook
  const workbook = XLSX.utils.book_new();

  // get the sanitized sheet name - use provided sheetName or fallback to fileName
  const safeSheetName = sanitizeSheetName(sheetName || fileName);

  // append the worksheet to the workbook with the sanitized name
  XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);

  // generate the XLSX file content (binary string)
  // 'bookType: "xlsx"' specifies the output format
  // 'type: "array"' generates an ArrayBuffer
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

  const excelMime =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  const blob = new Blob([excelBuffer], { type: excelMime });

  exportToFile(`${fileName}.xlsx`, blob, excelMime);
};

/**
 * Process and download a report based on its type
 * @param {Object} reportData Report data containing encoded content
 * @param {string} baseName Base filename for the report
 */
export const processReport = (reportData, baseName) => {
  const {
    pdf: encodedPdfData,
    excel: encodedExcelData,
    sheet_name: sheetName,
    csv: encodedCsvData,
    has_header: hasHeader,
    csv_separator,
  } = reportData;

  if (encodedCsvData) {
    processCsvReport(encodedCsvData, {
      fileName: baseName,
      hasHeader,
      csv_separator,
    });
    return true;
  }

  if (encodedPdfData) {
    processPdfReport(encodedPdfData, baseName);
    return true;
  }

  if (encodedExcelData) {
    return processExcelReport(
      encodedExcelData,
      baseName,
      sheetName,
      hasHeader !== false
    );
  }

  return false;
};

/**
 * Function to convert entity type to create title
 * @param {string} entityType - The entity type (e.g., 'system_group', 'cable_type')
 * @returns {string} - The formatted create title (e.g., 'Create System Group')
 */
export const getCreateTitle = (entityType) => {
  // Convert snake_case to Title Case
  const displayName = entityType
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return `Create ${displayName}`;
};

/**
 * Check if a route should be excluded from global object filtering
 * @param {string} pathname - The current route pathname
 * @param {string} mode - The filtering mode: 'create' for create routes, 'list' for overview routes
 * @returns {boolean} - True if the route should be excluded from global filtering
 */
export const isExcludedFromGlobalFiltering = (pathname, mode = "create") => {
  const excludedRoutes = {
    create: [ROUTES.object.create, ROUTES.cableType.create],
    list: [ROUTES.object.list, ROUTES.cableType.list, ROUTES.project.list],
  };

  const routes = excludedRoutes[mode] || excludedRoutes.create;

  return routes.some((route) =>
    pathname.toLowerCase().includes(route.toLowerCase())
  );
};

/**
 * Check if the global object filter dropdown should be disabled
 * based on the current route (create/edit screens)
 * @param {string} pathname - The current route pathname
 * @returns {boolean} - True if the global filter dropdown should be disabled
 */
export const shouldDisableGlobalFilter = (pathname) => {
  if (!pathname) return false;

  const lowerPath = pathname.toLowerCase();

  // Check for edit screens: pattern /entityName/:id (e.g., /cables/123, /objects/456)
  // Regex pattern to match: /word/guid-or-number (minimum 1 characters for ID)
  const editScreenPattern = /^\/[a-z-]+\/[a-z0-9-]+$/i;
  if (editScreenPattern.test(pathname)) {
    return true;
  }

  // Check for create screens: pattern /entityName/create (with optional query params)
  // e.g., /cables/create, /sections/create?objectId=123&systemId=456
  return lowerPath.includes("/create");

};

// Function to check if an entity is owned by OASIS
// It is regarded as owned by OASIS if the created_by field is not 'CODEX'
// When created by is 'CODEX', it means it was created by the Codex system and is managed externally.
export const ownedByOASIS = (data) => {
  return data?.created_by !== 'CODEX';
};
